import i18n from "@/i18n";
import { dash } from "@/helpers";

export const COUNT_UNIT = "product_unit";
export const WEIGHT_UNIT = "weight_unit";
export const LENGTH_UNIT = "length_unit";
export const VOLUME_UNIT = "volume_unit";

export const unitMap = Object.freeze({
  // count
  count: { id: "count", labelKey: "common.units.unit", type: COUNT_UNIT },
  unit: { id: "unit", labelKey: "common.units.unit", type: COUNT_UNIT },
  case: { id: "case", labelKey: "common.units.case", type: COUNT_UNIT },
  pack: { id: "pack", labelKey: "common.units.pack", type: COUNT_UNIT },
  bottle: { id: "bottle", labelKey: "common.units.bottle", type: COUNT_UNIT },
  box: { id: "box", labelKey: "common.units.box", type: COUNT_UNIT },
  cycle: { id: "cycle", labelKey: "common.units.cycle", type: COUNT_UNIT },
  bag: { id: "bag", labelKey: "common.units.bag", type: COUNT_UNIT },
  pallet: { id: "pallet", labelKey: "common.units.pallet", type: COUNT_UNIT },
  tray: { id: "tray", labelKey: "common.units.tray", type: COUNT_UNIT },
  can: { id: "can", labelKey: "common.units.can", type: COUNT_UNIT },
  panel: { id: "panel", labelKey: "common.units.panel", type: COUNT_UNIT },
  part: { id: "part", labelKey: "common.units.part", type: COUNT_UNIT },
  bend: { id: "bend", labelKey: "common.units.bend", type: COUNT_UNIT },
  // weight
  kg: { id: "kg", labelKey: "common.units.kilogram", type: WEIGHT_UNIT, conversionToStandard: 1.0 },
  g: { id: "g", labelKey: "common.units.gram", type: WEIGHT_UNIT, conversionToStandard: 0.001 },
  mt: { id: "mt", labelKey: "common.units.ton", type: WEIGHT_UNIT, conversionToStandard: 1000 },
  lb: { id: "lb", labelKey: "common.units.pound", type: WEIGHT_UNIT, conversionToStandard: 0.453592 },
  oz: { id: "oz", labelKey: "common.units.ounce", type: WEIGHT_UNIT, conversionToStandard: 0.0283495 },
  t: { id: "t", labelKey: "common.units.imperialTon", type: WEIGHT_UNIT, conversionToStandard: 1016.05 },
  kilogram: { id: "kg", labelKey: "common.units.kilogram", type: WEIGHT_UNIT, conversionToStandard: 1.0 },
  ton: { id: "mt", labelKey: "common.units.ton", type: WEIGHT_UNIT, conversionToStandard: 1000 },
  pound: { id: "lb", labelKey: "common.units.pound", type: WEIGHT_UNIT, conversionToStandard: 0.453592 },
  ounce: { id: "oz", labelKey: "common.units.ounce", type: WEIGHT_UNIT, conversionToStandard: 0.0283495 },
  // volume
  l: { id: "l", labelKey: "common.units.liter", type: VOLUME_UNIT, conversionToStandard: 1.0 },
  ml: { id: "ml", labelKey: "common.units.milliliter", type: VOLUME_UNIT, conversionToStandard: 0.001 },
  hl: { id: "hl", labelKey: "common.units.hectoliter", type: VOLUME_UNIT, conversionToStandard: 100.0 },
  m3: { id: "m3", labelKey: "common.units.cubicMeter", type: VOLUME_UNIT, conversionToStandard: 1000.0 },
  "fl-oz": { id: "oz", labelKey: "common.units.fluidOunce", type: VOLUME_UNIT, conversionToStandard: 0.0295735 },
  gal: { id: "gal", labelKey: "common.units.gallon", type: VOLUME_UNIT, conversionToStandard: 3.78541 },
  "gal-uk": { id: "uk", labelKey: "common.units.imperialGallon", type: VOLUME_UNIT, conversionToStandard: 4.54609 },
  liter: { id: "l", labelKey: "common.units.liter", type: VOLUME_UNIT, conversionToStandard: 1.0 },
  // length
  m: { id: "m", labelKey: "common.units.meter", type: LENGTH_UNIT, conversionToStandard: 1.0 },
  mm: { id: "mm", labelKey: "common.units.millimeter", type: LENGTH_UNIT, conversionToStandard: 0.001 },
  km: { id: "km", labelKey: "common.units.kilometer", type: LENGTH_UNIT, conversionToStandard: 1000.0 },
  in: { id: "in", labelKey: "common.units.inch", type: LENGTH_UNIT, conversionToStandard: 0.0254 },
  ft: { id: "ft", labelKey: "common.units.feet", type: LENGTH_UNIT, conversionToStandard: 0.3048 },
  mi: { id: "mi", labelKey: "common.units.mile", type: LENGTH_UNIT, conversionToStandard: 1609.34 },
  yd: { id: "yd", labelKey: "common.units.yard", type: LENGTH_UNIT, conversionToStandard: 0.9144 },
  meter: { id: "m", labelKey: "common.units.meter", type: LENGTH_UNIT, conversionToStandard: 1.0 },
  feet: { id: "ft", labelKey: "common.units.feet", type: LENGTH_UNIT, conversionToStandard: 0.3048 },
});

export const convertFromTo = (fromUnit, toUnit, value) => {
  const fromUnitDetails = unitMap[fromUnit.toLowerCase()];
  const toUnitDetails = unitMap[toUnit.toLowerCase()];
  if (!fromUnitDetails || !toUnitDetails) return null; // can't convert if one unit is not supported
  if (fromUnitDetails.type === COUNT_UNIT || toUnitDetails.type === COUNT_UNIT) return null; // can't convert count units
  if (fromUnitDetails.type !== toUnitDetails.type) return null; // can't convert if not the same unit type
  // 1. convert value in fromUnit to standard unit
  // 2. convert value in standard unit to toUnit
  return (value * fromUnitDetails.conversionToStandard) / toUnitDetails.conversionToStandard;
};

export const convertKilogramTo = (value, unit) => {
  return convertFromTo("kg", unit, value);
};

export const convertMeterTo = (value, unit) => {
  return convertFromTo("m", unit, value);
};

export const convertLiterTo = (value, unit) => {
  return convertFromTo("l", unit, value);
};

export const getUnitLabel = (unit) => {
  let labelKey = unitMap[unit]?.labelKey;
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return i18n.t(labelKey) ?? dash;
};
